import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import incotermsData from "./incotermsData.json";
import {
  BUYER_COLOR,
  SELLER_COLOR,
  STAGE_COLUMNS,
  TABLE_BORDER,
  TRANSPORT_MODES,
} from "./incotermsConstants";

const cellSx = {
  border: TABLE_BORDER,
  textAlign: "center",
  verticalAlign: "middle",
  p: 0.75,
};

const ResponsibilityBadge = ({ value }) => {
  if (value === "seller") {
    return (
      <Typography
        component="span"
        sx={{ color: SELLER_COLOR, fontWeight: 900, fontSize: "1.05rem", lineHeight: 1 }}
      >
        S
      </Typography>
    );
  }
  if (value === "buyer") {
    return (
      <Typography
        component="span"
        sx={{ color: BUYER_COLOR, fontWeight: 900, fontSize: "1.05rem", lineHeight: 1 }}
      >
        B
      </Typography>
    );
  }
  if (value === "shared") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.15 }}>
        <Typography
          component="span"
          sx={{ color: SELLER_COLOR, fontWeight: 900, fontSize: "1.05rem", lineHeight: 1 }}
        >
          S
        </Typography>
        <Typography
          component="span"
          sx={{ color: BUYER_COLOR, fontWeight: 900, fontSize: "1.05rem", lineHeight: 1 }}
        >
          B
        </Typography>
      </Box>
    );
  }
  return null;
};

const TransportModeIcons = ({ modes }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0.35 }}>
    {modes.map((mode) => {
      const config = TRANSPORT_MODES[mode];
      if (!config) return null;
      const Icon = config.icon;
      return (
        <Box
          key={mode}
          title={mode}
          sx={{
            width: 24,
            height: 24,
            bgcolor: config.color,
            borderRadius: 0.5,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ fontSize: 15, color: "#fff" }} />
        </Box>
      );
    })}
  </Box>
);

const IncotermsTable = () => {
  const { t } = useTranslation();
  const { incoterms } = incotermsData;

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Box
        component="table"
        sx={{
          width: "100%",
          minWidth: 1100,
          borderCollapse: "collapse",
          border: TABLE_BORDER,
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          fontSize: "0.68rem",
          bgcolor: "#fff",
        }}
      >
        <thead>
          <tr>
            <Box component="th" sx={{ ...cellSx, width: 88, bgcolor: "#f5f5f5" }} />
            <Box component="th" sx={{ ...cellSx, width: 72, bgcolor: "#f5f5f5" }} />
            {STAGE_COLUMNS.map((col) => {
              const Icon = col.icon;
              return (
                <Box
                  key={`icon-${col.field}`}
                  component="th"
                  sx={{
                    ...cellSx,
                    width: 62,
                    minWidth: 56,
                    bgcolor: col.iconBg,
                    py: 0.5,
                  }}
                >
                  <Icon sx={{ fontSize: 20, color: "#fff" }} />
                </Box>
              );
            })}
          </tr>
          <tr>
            <Box
              component="th"
              sx={{
                ...cellSx,
                fontWeight: 800,
                fontSize: "0.62rem",
                textTransform: "uppercase",
                bgcolor: "#fafafa",
                lineHeight: 1.15,
              }}
            >
              {t("incoterms.columns.incoterm")}
            </Box>
            <Box
              component="th"
              sx={{
                ...cellSx,
                fontWeight: 800,
                fontSize: "0.58rem",
                textTransform: "uppercase",
                bgcolor: "#fafafa",
                lineHeight: 1.15,
              }}
            >
              {t("incoterms.columns.transportMode")}
            </Box>
            {STAGE_COLUMNS.map((col) => (
              <Box
                key={`label-${col.field}`}
                component="th"
                sx={{
                  ...cellSx,
                  fontWeight: 700,
                  fontSize: "0.55rem",
                  textTransform: "uppercase",
                  bgcolor: "#fafafa",
                  lineHeight: 1.1,
                  px: 0.35,
                }}
              >
                {t(`incoterms.${col.labelKey}`)}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {incoterms.map((row) => (
            <tr key={row.code}>
              <Box component="td" sx={{ ...cellSx, bgcolor: "#fff" }}>
                <Typography
                  component="div"
                  sx={{ fontWeight: 900, fontSize: "0.85rem", lineHeight: 1.1 }}
                >
                  {row.code}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontSize: "0.52rem",
                    textTransform: "uppercase",
                    color: "text.secondary",
                    lineHeight: 1.15,
                    mt: 0.25,
                  }}
                >
                  {row.name}
                </Typography>
              </Box>
              <Box component="td" sx={{ ...cellSx, bgcolor: "#fff" }}>
                <TransportModeIcons modes={row.transportModes} />
              </Box>
              {STAGE_COLUMNS.map((col) => (
                <Box key={`${row.code}-${col.field}`} component="td" sx={{ ...cellSx, bgcolor: "#fff" }}>
                  <ResponsibilityBadge value={row.responsibilities[col.field]} />
                </Box>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 3,
          mt: 1.5,
          px: 0.5,
          flexWrap: "wrap",
          fontSize: "0.8rem",
        }}
      >
        <Typography sx={{ color: SELLER_COLOR, fontWeight: 700 }}>
          {t("incoterms.legend.seller")}
        </Typography>
        <Typography sx={{ color: BUYER_COLOR, fontWeight: 700 }}>
          {t("incoterms.legend.buyer")}
        </Typography>
      </Box>
    </Box>
  );
};

export default IncotermsTable;
